import '../../styles/leaflet.css';

import L, { Browser as LBrowser, LatLng, LatLngTuple } from 'leaflet';
import React, { useState } from 'react';
import { MapContainer as LMap, MapContainerProps, TileLayer, useMapEvent } from 'react-leaflet';
import { RoutalPalette } from '../../new_components';
import { getLeafletMarkerIconSizeAndAnchor, getMarkerIcon } from './MarkerIcon';

const MAP_MIN_ZOOM = 1;
const MAP_MAX_ZOOM = 19;

type MapProps = {
  markerPosition: LatLngTuple;
  onPositionChange: (position: LatLng) => void;
  zoomLevel?: number;
  height?: number | string;
  mapStyles?: React.CSSProperties;
};

/**
 * ======================= WARNING =======================
 * <StrictMode> renders twice in development mode.
 * This causes the map to be rendered twice, so the reducer
 * state is reset and map is lost.
 *
 * Please disable <StrictMode> when modifying map things.
 * Remember to enable it again when you are done.
 * ======================= WARNING =======================
 */
export const FixedMarkerMap = ({
  markerPosition,
  onPositionChange,
  zoomLevel = 17,
  height = 500,
  mapStyles,
}: MapProps & MapContainerProps) => {
  const markerPositionLatLng = L.latLng(markerPosition);
  const [center, setCenter] = useState<LatLng>(markerPositionLatLng);

  const handleOnPositionChange = (position: LatLng) => {
    onPositionChange(position);
    setCenter(position);
  };

  const generateMarkerIcon = () => {
    const html = getMarkerIcon({ color: RoutalPalette.routeBaseColor });
    const { iconSize } = getLeafletMarkerIconSizeAndAnchor({
      kind: `default`,
    });

    if (!iconSize) {
      return null;
    }

    return (
      <div
        style={{
          position: `absolute`,
          top: `calc(50% - ${iconSize[1] / 2}px)`,
          left: `calc(50% - ${iconSize[0] / 2}px)`,
          zIndex: 1000, // Ensure it's above the map
          pointerEvents: `none`, // Allow interaction with the map below
        }}
      >
        <div
          style={{
            width: iconSize[0],
            height: iconSize[1],
          }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    );
  };

  return (
    <div
      style={{
        position: `relative`,
        width: `100%`,
        height: typeof height === `number` ? `${height}px` : height,
        userSelect: `none`,
      }}
    >
      <LMap
        center={center}
        zoom={zoomLevel}
        style={{ height: typeof height === `number` ? `${height}px` : height, ...mapStyles }}
        zoomControl={false}
        maxZoom={MAP_MAX_ZOOM}
        minZoom={MAP_MIN_ZOOM}
        attributionControl={false}
      >
        <TileLayer
          attribution="&copy; <a href='http://www.openstreetmap.org/copyright'>OpenStreetMap</a> &copy; <a href='https://carto.com/attributions'>CARTO</a>"
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          detectRetina
          maxZoom={MAP_MAX_ZOOM + 1}
          maxNativeZoom={LBrowser.retina ? MAP_MAX_ZOOM - 1 : MAP_MAX_ZOOM}
        />
        <UpdateCenter setCenter={handleOnPositionChange} />
      </LMap>

      {generateMarkerIcon()}
    </div>
  );
};

interface UpdateCenterProps {
  setCenter: (center: LatLng) => void;
}

// Component to update the map center state
const UpdateCenter = ({ setCenter }: UpdateCenterProps) => {
  const map = useMapEvent(`moveend`, () => {
    setCenter(map.getCenter());
  });

  return null;
};
